import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MitigationPlanFragment,
  useRejectMitigationPlanMutation,
  useSignMitigationPlanMutation,
} from 'src/graphql';
import * as Types from '../../../types';

interface Props {
  plan: MitigationPlanFragment;
  onSigned?: () => void;
}

export const MitigationPlanApprovalChain = ({ plan, onSigned }: Props) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;
  const sp = tokens.semantic.spacing.desktop.gap;

  const currentUserId = useSelector(
    (state: Types.State) => state.globalUsers.currentUser?.id,
  );

  const signMutation = useSignMitigationPlanMutation({
    onSuccess: () => onSigned?.(),
  });

  const rejectMutation = useRejectMitigationPlanMutation({
    onSuccess: () => onSigned?.(),
  });

  const reviewers = plan.reviewerSignatories ?? [];

  const myRow = reviewers.find(
    (s) => currentUserId !== undefined && s.userId === Number(currentUserId),
  );
  const canDecide =
    myRow !== undefined &&
    !myRow.signedAt &&
    !myRow.rejectedAt &&
    (plan.status === 'PENDING' || plan.status === 'OVERDUE');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rejectionNote, setRejectionNote] = useState('');
  const isActing = signMutation.isLoading || rejectMutation.isLoading;

  if (reviewers.length === 0) {
    return (
      <Typography variant="body2" color={color.type.muted.value}>
        No reviewers assigned
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp[2].value }}>
      {canDecide && (
        <Box
          sx={{
            backgroundColor: color.surface.variant.value,
            borderRadius: tokens.semantic.radius.md.value,
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Your decision is required
          </Typography>
          <Typography variant="body2" color={color.type.muted.value}>
            Approve to accept the plan or reject to cancel it.
          </Typography>
          {(signMutation.isError || rejectMutation.isError) && (
            <Alert severity="error" sx={{ py: '2px' }}>
              {signMutation.isError
                ? 'Failed to approve. Please try again.'
                : 'Failed to reject. Please try again.'}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button
              variant="outlined"
              size="small"
              disabled={isActing}
              onClick={() => setConfirmOpen(true)}
              data-testid="reject-mitigation-plan-drawer-button"
              sx={{
                textTransform: 'none',
                borderColor: color.action.destructive.default.value,
                color: color.action.destructive.default.value,
                '&:hover': {
                  borderColor: color.action.destructive.default.value,
                  backgroundColor: 'transparent',
                },
              }}
            >
              {rejectMutation.isLoading ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              Reject
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={isActing}
              onClick={() => signMutation.mutate({ planId: plan.id })}
              data-testid="approve-mitigation-plan-button"
              sx={{ textTransform: 'none' }}
            >
              {signMutation.isLoading ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              Approve
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setRejectionNote('');
        }}
        data-testid="reject-mitigation-plan-confirm-dialog"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject mitigation plan?</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          <DialogContentText>
            Rejecting will keep the case under review. The reviewer lead can
            then assign a revised mitigation.
          </DialogContentText>
          <TextField
            label="Rejection reason"
            multiline
            minRows={3}
            required
            fullWidth
            value={rejectionNote}
            onChange={(e) => setRejectionNote(e.target.value)}
            inputProps={{ 'data-testid': 'rejection-note-input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              setRejectionNote('');
            }}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={rejectionNote.trim().length === 0}
            onClick={() => {
              const note = rejectionNote.trim();
              setConfirmOpen(false);
              setRejectionNote('');
              rejectMutation.mutate({ planId: plan.id, note });
            }}
            data-testid="reject-mitigation-plan-confirm-button"
            sx={{ textTransform: 'none', color: '#fff' }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          border: `1px solid ${color.ui.divider.default.value}`,
          borderRadius: tokens.semantic.radius.md.value,
          overflow: 'hidden',
        }}
      >
        {reviewers.map((s, i, arr) => (
          <ReviewerRow
            key={s.id}
            signatory={s}
            tokens={tokens}
            isLast={i === arr.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
};

interface ReviewerRowProps {
  signatory: MitigationPlanFragment['signatories'][number];
  tokens: LensTheme['tokens'];
  isLast: boolean;
}

const ReviewerRow = ({ signatory, tokens, isLast }: ReviewerRowProps) => {
  const color = tokens.semantic.color;
  const name =
    signatory.user?.displayName ||
    signatory.user?.name ||
    `User ${signatory.userId}`;
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((n: string) => n[0])
    .join('');
  const signed = Boolean(signatory.signedAt);
  const rejected = Boolean(signatory.rejectedAt);
  const decisionDate =
    signatory.signedAt || signatory.rejectedAt
      ? new Date(
          (signatory.signedAt ?? signatory.rejectedAt) as string,
        ).toLocaleDateString()
      : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: isLast
          ? 'none'
          : `1px solid ${color.ui.divider.default.value}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: '12px' }}>
          {initials}
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {name}
          </Typography>
          {decisionDate && (
            <Typography variant="caption" color={color.type.muted.value}>
              {signed ? 'Approved' : 'Rejected'} {decisionDate}
            </Typography>
          )}
          {rejected && signatory.rejectionNote && (
            <Typography variant="caption" color={color.type.muted.value}>
              {signatory.rejectionNote}
            </Typography>
          )}
        </Box>
      </Box>
      {signed ? (
        <CheckCircleOutlineIcon
          fontSize="small"
          sx={{ color: color.status.success.default.value }}
          aria-label="Approved"
        />
      ) : rejected ? (
        <CancelOutlinedIcon
          fontSize="small"
          sx={{ color: color.action.destructive.default.value }}
          aria-label="Rejected"
        />
      ) : (
        <PendingOutlinedIcon
          fontSize="small"
          sx={{ color: color.type.muted.value }}
          aria-label="Pending"
        />
      )}
    </Box>
  );
};
